import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { KeyRound, Loader2, Clock, CheckCircle2, XCircle, LogOut, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// WhatsApp number for the admin - change this to the real number
const ADMIN_WHATSAPP = "971000000000";

const ClaimAccount = () => {
  const { user, isAdmin, isMember, claimStatus, isLoading, signOut, refreshClaimStatus } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) navigate("/login");
    if (!isLoading && user && isAdmin) navigate("/admin");
    if (!isLoading && user && isMember && claimStatus === "approved") navigate("/my-account");
  }, [user, isAdmin, isMember, claimStatus, isLoading, navigate]);

  // Poll for status updates
  useEffect(() => {
    if (claimStatus !== "pending") return;
    const interval = setInterval(() => refreshClaimStatus(), 5000);
    return () => clearInterval(interval);
  }, [claimStatus, refreshClaimStatus]);

  const handleSubmitClaim = async () => {
    const trimmedCode = code.trim().toUpperCase();
    if (trimmedCode.length !== 6) {
      toast({ title: "الكود غير صحيح", description: "يجب أن يكون الكود مكوناً من 6 أحرف وأرقام", variant: "destructive" });
      return;
    }

    setSubmitting(true);

    const { data: member, error: memberError } = await supabase
      .from("family_members")
      .select("id, name, member_code")
      .eq("member_code", trimmedCode)
      .maybeSingle();

    if (memberError || !member) {
      toast({ title: "لم يتم العثور على الكود", description: "تأكد من الكود الذي منحك إياه المشرف", variant: "destructive" });
      setSubmitting(false);
      return;
    }

    const { error } = await supabase.from("member_claims").insert({
      user_id: user!.id,
      member_id: member.id,
      member_code: trimmedCode,
    });

    setSubmitting(false);

    if (error) {
      if (error.message.includes("duplicate")) {
        toast({ title: "لديك طلب مسبق", description: "يوجد طلب مطالبة قائم بالفعل", variant: "destructive" });
      } else {
        toast({ title: "حدث خطأ", description: error.message, variant: "destructive" });
      }
    } else {
      toast({ title: "تم إرسال الطلب", description: `طلب المطالبة بحساب ${member.name} قيد المراجعة` });
      refreshClaimStatus();
    }
  };

  const whatsappLink = `https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent("السلام عليكم، أنا أحد أفراد عائلة أبوليلة وأريد تفعيل حسابي والحصول على الكود التعريفي الخاص بي.")}`;

  if (isLoading) return null;

  // Pending status
  if (claimStatus === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-amber-500/10 w-fit">
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
              <CardTitle className="font-amiri text-2xl gold-text">طلبك قيد المراجعة</CardTitle>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                تم إرسال طلب المطالبة بحسابك إلى المشرف
                <br />
                سيتم إشعارك عند الموافقة على طلبك
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <Button variant="outline" className="w-full rounded-xl" onClick={signOut}>
                <LogOut className="h-4 w-4 ml-2" />
                تسجيل الخروج
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Rejected status
  if (claimStatus === "rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="glass-card border-destructive/20 rounded-2xl modern-shadow">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-destructive/10 w-fit">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="font-amiri text-2xl text-destructive">تم رفض الطلب</CardTitle>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                تم رفض طلب المطالبة بحسابك. تواصل مع المشرف لمزيد من المعلومات.
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <Button asChild className="w-full rounded-xl bg-green-600 hover:bg-green-700 text-white">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4 ml-2" />
                  تواصل مع المشرف عبر واتساب
                </a>
              </Button>
              <Button variant="outline" className="w-full rounded-xl" onClick={signOut}>
                <LogOut className="h-4 w-4 ml-2" />
                تسجيل الخروج
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Approved - redirect to dashboard
  if (claimStatus === "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="glass-card border-green-500/20 rounded-2xl modern-shadow">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-green-500/10 w-fit">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle className="font-amiri text-2xl gold-text">تمت الموافقة!</CardTitle>
              <p className="text-muted-foreground mt-3 text-sm">تم ربط حسابك بنجاح. يمكنك الآن الوصول لمعلومات العائلة.</p>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <Button className="w-full gold-gradient text-primary-foreground rounded-xl" onClick={() => navigate("/my-account")}>
                لوحة التحكم الخاصة بي
              </Button>
              <Button variant="outline" className="w-full rounded-xl" onClick={() => navigate("/tree")}>
                عرض شجرة العائلة
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Claim form (status === "none")
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
        <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
              <KeyRound className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="font-amiri text-2xl gold-text">المطالبة بحسابك</CardTitle>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
              أدخل الكود الخاص بك المكوّن من 6 أحرف وأرقام
              <br />
              الذي منحك إياه مشرف العائلة
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>كود الفرد</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder="مثال: A1B2C3"
                  className="bg-secondary/50 border-primary/10 rounded-xl text-center text-2xl font-mono tracking-[0.3em] uppercase"
                  dir="ltr"
                  maxLength={6}
                />
              </div>
              <Button 
                className="w-full gold-gradient text-primary-foreground rounded-xl" 
                disabled={submitting || code.trim().length !== 6}
                onClick={handleSubmitClaim}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                طلب استرداد البيانات
              </Button>

              {/* WhatsApp contact section */}
              <div className="rounded-xl border border-primary/10 bg-secondary/30 p-4 text-center space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  ليس لديك الكود؟ تواصل مع المشرف عبر واتساب
                  <br />
                  لتعريف نفسك والحصول على كود التفعيل
                </p>
                <Button asChild variant="outline" className="w-full rounded-xl border-green-600/30 text-green-500 hover:bg-green-600/10 hover:text-green-400">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4 ml-2" />
                    مراسلة المشرف على واتساب
                  </a>
                </Button>
              </div>

              <Button variant="outline" className="w-full rounded-xl" onClick={signOut}>
                <LogOut className="h-4 w-4 ml-2" />
                تسجيل الخروج
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ClaimAccount;
